import { useEffect, useRef, type ReactNode } from "react";
import anime from "animejs";

/**
 * Stagger — wraps a list of elements and animates them in with stagger.
 *
 * Children are hidden via CSS rule `[data-animate-pending] > * { opacity: 0 }`
 * which applies before any JS runs — zero flash guaranteed.
 * The data attribute is removed when the animation starts.
 */
type DivProps = Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "className">;

interface StaggerProps extends DivProps {
  children: ReactNode;
  className?: string;
  gap?: number; // ms between items (default 50)
  delay?: number; // initial delay before first item (default 80)
  enabled?: boolean; // trigger animation when true
  distance?: number; // translateY distance in px (default 12)
}

export function Stagger({
  children,
  className,
  gap = 50,
  delay = 80,
  enabled = true,
  distance = 12,
  ...rest
}: StaggerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || animated.current) return;
    const targets = el.children;
    if (targets.length === 0) return;
    animated.current = true;

    // Set initial transform (CSS only hides opacity)
    anime.set(targets, { translateY: distance });

    // Add will-change for GPU compositing
    for (const child of Array.from(targets)) {
      (child as HTMLElement).style.willChange = "transform, opacity";
    }

    // Remove CSS hiding — anime.js takes over from opacity: 0
    el.removeAttribute("data-animate-pending");

    anime({
      targets,
      opacity: [0, 1],
      translateY: [distance, 0],
      easing: "easeOutCubic",
      duration: 400,
      delay: anime.stagger(gap, { start: delay }),
      complete: () => {
        for (const child of Array.from(targets)) {
          const c = child as HTMLElement;
          c.style.willChange = "";
          c.style.opacity = "";
          c.style.transform = "";
        }
      },
    });
  }, [enabled, gap, delay, distance]);

  // data-animate-pending is only added before first animation
  const pending = !animated.current;

  return (
    <div
      ref={ref}
      className={className}
      {...(pending ? { "data-animate-pending": "" } : {})}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * FadeIn — wraps a single section and fades it in on mount.
 * Hidden via CSS `[data-animate-self-pending] { opacity: 0 }`.
 */
interface FadeInProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  enabled?: boolean;
  distance?: number;
}

export function FadeIn({
  children,
  className,
  delay = 0,
  enabled = true,
  distance = 16,
}: FadeInProps) {
  const ref = useRef<HTMLDivElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || animated.current) return;
    animated.current = true;

    anime.set(el, { translateY: distance });
    el.style.willChange = "transform, opacity";

    // Remove CSS hiding — anime.js takes over
    el.removeAttribute("data-animate-self-pending");

    anime({
      targets: el,
      opacity: [0, 1],
      translateY: [distance, 0],
      easing: "easeOutCubic",
      duration: 450,
      delay,
      complete: () => {
        el.style.willChange = "";
        el.style.opacity = "";
        el.style.transform = "";
      },
    });
  }, [enabled, delay, distance]);

  const pending = !animated.current;

  return (
    <div
      ref={ref}
      className={className}
      {...(pending ? { "data-animate-self-pending": "" } : {})}
    >
      {children}
    </div>
  );
}
